import { Button, Flex, HStack, Image, Portal, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { DATATABLE_COLLAPSE, DATATABLE_EXPAND } from 'constants/mixpanel';
import { registerRenderer, textRenderer } from 'handsontable/renderers';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import cloneDeep from 'lodash.clonedeep';
import { ColorPicker, StepColorPickerContainer } from 'modules/Editor/components/ColorPicker';
import {
  ChartSettings,
  ChartSettingsColorPicker,
  ChartSettingsDivider,
  ChartSettingsEntry,
  ChartSliderInput,
  ChartStyle,
  DataTab,
  GridLines,
  Legend,
  MapType as MapTypeComponent,
  NumberFormat,
  SeriesColorPicker,
  SetupTab,
  Switcher,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { DataTabTable } from 'modules/Editor/components/SideMenuSettings/ChartSettings/DataTabTable';
import { DataTabTableProps } from 'modules/Editor/components/SideMenuSettings/ChartSettings/DataTabTable.types';
import { MapType } from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { SidePanelHeader } from 'modules/common/components/SidePanel';
import { DropdownPopover, stopPropagationHandler } from 'modules/common/components/ToolbarPopover';
import { ReactElement, ReactNode, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import {
  ALLOW_SINGLE_SERIES_CHARTS,
  FORCE_SINGLE_SERIES_CHARTS,
  SINGLE_COLUMN_CHARTS,
} from 'widgets/ChartWidgets/ChartWidget.config';
import { useHighchartsData } from 'widgets/ChartWidgets/ChartWidget.hooks';
import { ChartWidgetData, HighChartsWidgetTypes } from 'widgets/ChartWidgets/ChartWidget.types';
import {
  getMapTypeButtonImage,
  getRandomPatterns,
  isMultiSeriesChart,
} from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommon.helpers';
import {
  LegendFontColor,
  LegendFontSettings,
  LegendFontStyle,
  LegendFontWeight,
} from 'widgets/ChartWidgets/DataTableChartCommon/TextStyleSettings';
import { MapWidgetData } from 'widgets/MapWidget/MapWidget.types';
import { SIDEMENU_WIDGET_TITLE_ID } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { GetWidgetMemberComponent } from 'widgets/Widget.types';
import { useEditor, useSideMenuSetting, useWidget } from 'widgets/sdk';

import { AxisSetting } from './ChartSettingsAxis';
import { LabelSetting, LabelSettingWithoutAxis } from './ChartSettingsLabel';
import { DataLabelAdornment, DataLabelFormat, DataNumberFormat } from './ChartSettingsNumberFormat';
import {
  BORDER_WIDTH_RANGE_MAX,
  BORDER_WIDTH_RANGE_MIN,
  COLOR_STEPS_OPTIONS,
  COLOR_STEPS_OPTIONS_OFFSET,
} from './DataTableChartCommon.config';
import { CellProps, ChartDataTableProps } from './DataTableChartCommon.types';
import { DataTableExpandModal } from './DataTableExpandModal';
import { ImportData } from './ImportData';

interface WidgetMenuSettingsProps {
  /**
   * On default 'Edit Chart' is used as the title, map settings requires another custom name.
   */
  alternateTitle?: string;
  /**
   * charts may have different axis setting, this is used to replace the default one.
   * Ex: the combo chart would have two y-axis
   */
  customAxisConfiguration?: ReactNode;
  customChartStyle?: ReactNode;
  customDataTab?: ReactNode;
  customLabelConfiguration?: ReactNode;

  /**
   * Chart can configure a custom number format section in the data tab instead of using the default one
   */
  customNumberFormat?: ReactNode;
  customSetupTab?: ReactNode;
  customSetupCategory?: ReactNode;
  disableAddColumn?: boolean;
  disableAddRow?: boolean;
  disableRemoveColumn?: boolean;
  disableRemoveRow?: boolean;
  headerColumnNumber?: number;
  headerRowNumber?: number;
  widgetId?: WidgetId;
  showDataLabels?: boolean;
  showMapType?: boolean;

  /**
   * In general, we only allow users to configure the y-axis range.
   * For example, we can let a bar chart to draw the bar from a 50 instead of default 0, and to 500 when the max value is 150
   *
   * Show dual range configuration will allow users to configure the x-axis range as well.
   * This is used to opt out some of the outlier.
   * Ex: data can have year range from 2015 to 2020, but we can config to show data from 2016 to 2019
   */
  showDualRangeConfiguration?: boolean;
  showAxisConfiguration?: boolean;
  showImportData?: boolean;
  showNumberFormatSwitch?: boolean;
  showLabelsConfiguration?: boolean;
  showLegendsConfiguration?: boolean;
  showGridLinesConfiguration?: boolean;
  showBorderColor?: boolean;
  showBorderWidth?: boolean;
  showDonutHole?: boolean;
  showPatterns?: boolean;
  showStepColors?: boolean;

  /**
   * The data table may require a set of setting specific to the widget type
   */
  dataTableProps?: Partial<DataTabTableProps>;
  /**
   * The getWidgetMemberComponent is passed to widgets as props
   * this method is used to passed down to 'DataTableChartMenuSettings' and 'DataTableExpandModal'
   * then it is being used to render the widget.
   * The reason of passing it as props compared to reuse the 'WidgetRenderer' is to avoid depcruise no-circular error.
   * For the group widget to get widget components and render widgets in 'WidgetBase'
   */
  getWidget?: GetWidgetMemberComponent;
}

export const DataTableChartMenuSettings = ({
  showDualRangeConfiguration,
  customAxisConfiguration,
  customChartStyle,
  customDataTab,
  customLabelConfiguration,
  customNumberFormat,
  customSetupTab,
  customSetupCategory,
  dataTableProps,
  disableAddColumn,
  disableAddRow,
  disableRemoveColumn,
  disableRemoveRow,
  headerColumnNumber,
  headerRowNumber,
  showAxisConfiguration = false,
  showMapType = false,
  showDataLabels = false,
  showImportData = true,
  showNumberFormatSwitch = false,
  showLabelsConfiguration = false,
  showLegendsConfiguration = false,
  showGridLinesConfiguration = false,
  showBorderColor = false,
  showBorderWidth = false,
  showDonutHole = false,
  showPatterns = false,
  showStepColors = false,
  alternateTitle,
  getWidget,
}: WidgetMenuSettingsProps): ReactElement => {
  const { closeWidgetSideMenu, setChartWidgetSettingTabIndex, chartSettingsTabIndex } = useEditor();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false });

  const { widgetId } = useWidget<ChartWidgetData>();
  const widgetType = useMemo(() => getWidgetTypeFromId(widgetId), [widgetId]);
  const isMapChart = widgetType === WidgetType.MapChart;
  const showDefaultNumberFormat = !customNumberFormat && showDataLabels;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { disableEditorKeyboardShortcuts, enableEditorKeyboardShortcuts } = useEditor();

  // We focus on the header close button to skip the initial focus on Importing csv/xlsx files
  // and allow tabbing to the Data/Setup tab.
  const headerCloseButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenDataTableModal = () => {
    disableEditorKeyboardShortcuts();
    Mixpanel.track(DATATABLE_EXPAND.eventName);
    onOpen();
  };

  const handleCloseDataTableModal = () => {
    enableEditorKeyboardShortcuts();
    Mixpanel.track(DATATABLE_COLLAPSE.eventName);
    onClose();
  };

  useEffect(() => {
    if (headerCloseButtonRef.current) headerCloseButtonRef.current?.focus();
  }, [headerCloseButtonRef]);

  return (
    <>
      <SidePanelHeader
        title={alternateTitle || t('chartSettings.editChartTitle')}
        onClose={closeWidgetSideMenu}
        ref={headerCloseButtonRef}
        titleId={SIDEMENU_WIDGET_TITLE_ID}
      />
      <ChartSettings selectedTabIndex={chartSettingsTabIndex} setSelectedTabIndex={setChartWidgetSettingTabIndex}>
        <DataTab>
          {customDataTab ? (
            chartSettingsTabIndex === 0 && customDataTab
          ) : (
            <>
              {chartSettingsTabIndex === 0 && showImportData && (
                <ImportData
                  headerRowNumber={headerRowNumber}
                  dataTableProps={dataTableProps}
                  skipColorUpdate={isMapChart}
                  shouldUpdateDataClasses={isMapChart}
                />
              )}
              {showDefaultNumberFormat && (
                <NumberFormat>
                  {showNumberFormatSwitch && <DataNumberFormat />}
                  <DataLabelAdornment />
                  <DataLabelFormat />
                </NumberFormat>
              )}
              {customNumberFormat}
              {showMapType && (
                <MapTypeComponent>
                  <MapTypeButtons />
                </MapTypeComponent>
              )}
              {chartSettingsTabIndex === 0 && (
                <>
                  {/* Render DataTableExpandModal */}
                  {getWidget && (
                    <>
                      <Portal>
                        <DataTableExpandModal
                          isOpen={isOpen}
                          onClose={handleCloseDataTableModal}
                          getWidget={getWidget}
                          component={
                            <ChartDataTable
                              disableAddColumn={disableAddColumn}
                              disableAddRow={disableAddRow}
                              disableRemoveColumn={disableRemoveColumn}
                              disableRemoveRow={disableRemoveRow}
                              headerColumnNumber={headerColumnNumber}
                              headerRowNumber={headerRowNumber}
                              skipColorUpdate={isMapChart}
                              shouldUpdateDataClasses={isMapChart}
                              isMapChart={isMapChart}
                              {...dataTableProps}
                            />
                          }
                        />
                      </Portal>
                      <Flex justifyContent='flex-end'>
                        <Button onClick={handleOpenDataTableModal} variant={'outline'} size={'sm'}>
                          {t('chartSettings.expandButton')}
                        </Button>
                      </Flex>
                    </>
                  )}
                  <ChartDataTable
                    disableAddColumn={disableAddColumn}
                    disableAddRow={disableAddRow}
                    disableRemoveColumn={disableRemoveColumn}
                    disableRemoveRow={disableRemoveRow}
                    headerColumnNumber={headerColumnNumber}
                    headerRowNumber={headerRowNumber}
                    skipColorUpdate={isMapChart}
                    shouldUpdateDataClasses={isMapChart}
                    isMapChart={isMapChart}
                    {...dataTableProps}
                  />
                </>
              )}
            </>
          )}
        </DataTab>
        <SetupTab>
          {customSetupTab ? (
            customSetupTab
          ) : (
            <>
              <ChartStyle isDefaultOpen>
                {showStepColors && <StepSeriesColor />}
                {showStepColors && <ColorSteps />}
                {!showStepColors && <SeriesColor />}
                {(showBorderColor || showBorderWidth) && <ChartSettingsDivider />}
                {showBorderColor && <BorderColor />}
                {showBorderWidth && <BorderWidth />}
                {(showDonutHole || showPatterns) && <ChartSettingsDivider />}
                {showDonutHole && <DonutHole />}
                {showPatterns && <Patterns />}
                {customChartStyle}
              </ChartStyle>
              {showGridLinesConfiguration && (
                <GridLines>
                  <GridLineColor />
                </GridLines>
              )}
              {showAxisConfiguration && (
                <AxisSetting showDualRangeConfiguration={showDualRangeConfiguration} widgetType={widgetType} />
              )}
              {customAxisConfiguration}
              <ChartDataLabel
                showAxisConfiguration={showAxisConfiguration}
                showLabelsConfiguration={!customLabelConfiguration && showLabelsConfiguration}
                widgetType={widgetType}
              />
              {customLabelConfiguration}
              {showLegendsConfiguration && <LegendConfig />}
              {customSetupCategory}
            </>
          )}
        </SetupTab>
      </ChartSettings>
    </>
  );
};

const ChartDataLabel = ({
  showAxisConfiguration = false,
  showLabelsConfiguration = false,
  widgetType,
}: {
  showAxisConfiguration: boolean;
  showLabelsConfiguration: boolean;
  widgetType: WidgetType;
}) => {
  // If we need the label config with axis labels config, render the LabelSetting
  if (showLabelsConfiguration && showAxisConfiguration) return <LabelSetting widgetType={widgetType} />;

  // If we need the label config but not axis labels config.
  if (showLabelsConfiguration) return <LabelSettingWithoutAxis widgetType={widgetType} />;

  // If showLabelsConfiguration flag is false
  return null;
};

export const ChartDataTable = ({
  disableAddColumn,
  disableAddRow,
  disableRemoveColumn,
  disableRemoveRow,
  headerColumnNumber,
  headerRowNumber,
  skipColorUpdate,
  shouldUpdateDataClasses,
  isMapChart,
  ...otherProps
}: ChartDataTableProps) => {
  const { chartSettingsTabIndex } = useEditor();
  const { widgetId, seriesMeta, seriesData, updateWidget } = useWidget<ChartWidgetData>();
  const chartWidgetType = getWidgetTypeFromId(widgetId) as HighChartsWidgetTypes;

  const optionRef = useRef({ seriesMeta, forceSingleSeries: true });
  optionRef.current = { seriesMeta, forceSingleSeries: true };

  registerRenderer('columnHeaderRenderer', (hotInstance, TD, ...rest): void => {
    textRenderer(hotInstance, TD, ...rest);
    TD.style.fontWeight = 'bold';
  });

  const cellRenderer = (row: number, column: number) => {
    const isMultiSeries = isMultiSeriesChart(seriesData);
    const isHistogram = chartWidgetType === WidgetType.HistogramChart;

    const cellProperties = {} as CellProps;

    // apply columnHeader styling if first row and not histogram or map charts
    if (row === 0 && !isHistogram && !isMapChart) cellProperties.renderer = 'columnHeaderRenderer';

    // apply columnHeader styling if first column and chart data is multi series and not histogram or map charts
    if (column === 0 && isMultiSeries && !isHistogram && !isMapChart) {
      cellProperties.renderer = 'columnHeaderRenderer';
    }
    return cellProperties;
  };

  return (
    <DataTabTable
      cells={cellRenderer}
      disableAddColumn={disableAddColumn}
      disableAddRow={disableAddRow}
      disableRemoveColumn={disableRemoveColumn}
      disableRemoveRow={disableRemoveRow}
      headerColumnNumber={headerColumnNumber}
      headerRowNumber={headerRowNumber}
      // DataTabTable requires a non-proxy seriesData object to work properly
      // TODO: see if there's a better way to do this
      seriesData={cloneDeep(seriesData)}
      optionRef={optionRef}
      updateWidget={updateWidget}
      doTableReloadWorkaround={chartSettingsTabIndex === 0}
      // For now, the chart with no header column will not need spare columns on the table
      skipColorUpdate={skipColorUpdate}
      shouldUpdateDataClasses={shouldUpdateDataClasses}
      {...otherProps}
    />
  );
};

// Todo: decide whether this is the best place to keep this map widget only component
// Should it be within the map widget folder or together with ChartOptions?
const MapTypeButtons = () => {
  const { mapType, updateWidget } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false });

  const onMapTypeUpdate = useCallback(
    (mapType: MapType) => {
      updateWidget({ mapType });
    },
    [updateWidget],
  );

  const mapTypes = useMemo(() => Object.values(MapType), []);

  return (
    <HStack justifyContent={'center'} gap={'0.75rem'}>
      {mapTypes.map((mapTypeName, i) => {
        const onButtonClick = () => {
          onMapTypeUpdate(mapTypeName);
        };
        return (
          <VStack as='button' w={'6.5rem'} h={'fit-content'} onClick={onButtonClick} key={i}>
            <Image
              borderRadius={'base'}
              transitionDuration='100ms'
              outline={mapType === mapTypeName ? '2px solid' : '1px solid'}
              outlineColor={mapType === mapTypeName ? 'upgrade.500' : 'gray.100'}
              src={getMapTypeButtonImage(mapTypeName)}
              draggable={false}
            />
            <Text fontSize={'sm'} fontFamily={'Inter'} as={'span'}>
              {t(`mapChartSettings.${mapTypeName.toLowerCase()}Label` as keyof typeof t)}
            </Text>
          </VStack>
        );
      })}
    </HStack>
  );
};

export const BorderColor = () => {
  const {
    updateWidget,
    generalOptions: { borderColor },
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'chartSettings' });

  const onChangeBorderColor = (color: string) => updateWidget({ generalOptions: { borderColor: color } });

  return <ChartSettingsColorPicker label={t('borderColorLabel')} color={borderColor} onChange={onChangeBorderColor} />;
};

const SeriesColor = () => {
  const { widgetId, seriesData, seriesMeta, updateWidget } = useWidget<ChartWidgetData>();
  const { colors } = seriesMeta;
  const colorsRef = useRef(colors);
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'chartSettings' });

  useEffect(() => {
    colorsRef.current = colors;
  }, [colors]);

  const onSetSeriesColor = useCallback(
    (color: string, index: number) => {
      if (colorsRef.current[index] !== color) {
        const newColors = [...colorsRef.current];
        newColors[index] = color;
        updateWidget({ seriesMeta: { colors: newColors } });
      }
    },
    [updateWidget],
  );

  const widgetType = getWidgetTypeFromId(widgetId);
  const allowSingleSeries = ALLOW_SINGLE_SERIES_CHARTS.includes(widgetType);
  const forceSingleSeries = FORCE_SINGLE_SERIES_CHARTS.includes(widgetType);
  const isSingleColumnCharts = SINGLE_COLUMN_CHARTS.includes(widgetType);

  const { colorPickerSeries } = useHighchartsData({
    seriesData,
    seriesMeta,
    allowSingleSeries,
    forceSingleSeries,
    disableRowHeader: isSingleColumnCharts,
  });

  return <SeriesColorPicker label={t('chartStyleColorLabel')} series={colorPickerSeries} onChange={onSetSeriesColor} />;
};

const ColorSteps = () => {
  const { seriesMeta, updateWidget } = useWidget<ChartWidgetData>();
  const { colorSteps = COLOR_STEPS_OPTIONS_OFFSET } = seriesMeta;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'chartSettings' });

  const onChangeColorSteps = useCallback(
    (steps: string) => {
      updateWidget({ seriesMeta: { colorSteps: Number(steps) + COLOR_STEPS_OPTIONS_OFFSET } });
    },
    [updateWidget],
  );

  // No need to check the dependency array as the handler has no dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyPress = useCallback(stopPropagationHandler(triggerRef), []);

  return (
    <ChartSettingsEntry label={t('colorStepsTitle')} containerProps={{ marginTop: 8, marginBottom: 2 }}>
      <DropdownPopover
        label=''
        optionProps={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        containerProps={{
          onKeyDown: handleKeyPress,
          onKeyUp: handleKeyPress,
          'data-testid': 'brand-selector',
        }}
        triggerProps={{
          'data-testid': 'color-steps-trigger',
          ref: triggerRef,
          sx: {
            '& p': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
        }}
        closeOnSelect
        placement={'bottom'}
        returnFocusOnClose={false}
        selectedIndex={colorSteps - COLOR_STEPS_OPTIONS_OFFSET}
        onSelect={onChangeColorSteps}
        options={COLOR_STEPS_OPTIONS}
      />
    </ChartSettingsEntry>
  );
};

export const StepSeriesColor = () => {
  const { seriesMeta, updateWidget } = useWidget<ChartWidgetData>();
  const { colors, colorSteps } = seriesMeta;

  const { setAllowScrolling } = useSideMenuSetting();

  const colorsRef = useRef(colors);
  colorsRef.current = colors;

  const onSetSeriesColor = useCallback(
    (color: string, index: number) => {
      if (colorsRef.current[index] !== color) {
        const newColors = [...colorsRef.current];
        newColors[index] = color;
        updateWidget({ seriesMeta: { colors: newColors } });
      }
    },
    [updateWidget],
  );

  const colorsWithSteps = colors.slice(0, colorSteps ?? 0);

  return (
    <StepColorPickerContainer>
      {colorsWithSteps.map((color, index) => {
        const label = `Color ${color}`;

        const onChange = (color: string) => onSetSeriesColor(color, index);
        return (
          <ColorPicker
            key={`color-${index}`}
            color={color}
            label={label}
            onChange={onChange}
            iconStyle='step'
            onOpen={setAllowScrolling.off}
            onClose={setAllowScrolling.on}
            placement='left-start'
          />
        );
      })}
    </StepColorPickerContainer>
  );
};

export const BorderWidth = memo(() => {
  const {
    updateWidget,
    generalOptions: { borderWidth },
  } = useWidget<ChartWidgetData>();

  // Note: there is a border with translation on the editor_toolbar.
  // the only different is that we use the sentence case on all chart settings.
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'border' });
  const title = t('widthLabel');

  const onChangeBorderWidth = (width: number) => updateWidget({ generalOptions: { borderWidth: width } });

  return (
    <ChartSliderInput
      value={borderWidth}
      title={title}
      maxValue={BORDER_WIDTH_RANGE_MAX}
      minValue={BORDER_WIDTH_RANGE_MIN}
      onChange={onChangeBorderWidth}
    />
  );
});
BorderWidth.displayName = 'BorderWidth';

const DonutHole = () => {
  const {
    updateWidget,
    generalOptions: { innerSize },
  } = useWidget<ChartWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'pie' });
  const title = t('donutHole');

  const onChangeInnerSizePercentage = (innerSize: number) => updateWidget({ generalOptions: { innerSize } });

  return (
    <ChartSliderInput
      value={innerSize}
      title={title}
      maxValue={100}
      minValue={0}
      onChange={onChangeInnerSizePercentage}
      suffix={'%'}
    />
  );
};

const GridLineColor = () => {
  const {
    updateWidget,
    grid: {
      style: { lineColor },
    },
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'chartSettings' });

  const onChangeGridLineColor = (color: string) => updateWidget({ grid: { style: { lineColor: color } } });

  return (
    <ChartSettingsColorPicker label={t('gridLinesColorLabel')} color={lineColor} onChange={onChangeGridLineColor} />
  );
};

const LegendConfig = () => {
  const {
    updateWidget,
    legend: { enabled },
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'chartSettings' });

  const onToggleLegend = () => updateWidget({ legend: { enabled: !enabled } });

  return (
    <Legend onToggle={onToggleLegend} isChecked={enabled}>
      <LegendFontSettings />
      <ChartSettingsEntry label={t('textStyleLabel')}>
        <LegendFontColor />
        <LegendFontWeight />
        <LegendFontStyle />
      </ChartSettingsEntry>
    </Legend>
  );
};

const Patterns = () => {
  const {
    patterns: { enabled: isPatternsEnabled },
    updateWidget,
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', { useSuspense: false, keyPrefix: 'chartSettings' });

  const onTogglePatterns = () => {
    updateWidget({ patterns: { enabled: !isPatternsEnabled, list: !isPatternsEnabled ? getRandomPatterns() : [] } });
  };

  return <Switcher label={t('showPatternsLabel')} onToggle={onTogglePatterns} isChecked={isPatternsEnabled} />;
};
